import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Descriptions, Modal, Select, Space, message, Image } from 'antd';
import { CloseOutlined, FormOutlined, FileTextOutlined, PictureOutlined } from '@ant-design/icons';
import { useAuth } from '../../../components/Layout/AuthContext';
import { useStaffMap } from '../../../services/store/organization.store';
import { orderApi } from '../../../services/process/order';
import { externalReportApi } from '../../../services/process/external-report';
import type { MergedOrder, OrderEntity } from '../../../types/process/order';
import type { OrderListActionContext } from './OrderListTemplate';
import CancelOrderModal, { type CancelOrderModalRef, type CancelOrderTemplateKey } from '../modals/CancelOrderModal';
import DecisionModal, { type DecisionModalProps } from '../decision/components/DecisionModal';
import DecisionInfoModal from '../delivery/components/DecisionInfoModal';
import { showActionFeedback } from '../modals/ActionFeedback';
import PlatformLogModal, { type PlatformLogModalProps } from '../modals/PlatformLogModal';
import { OrderStatus } from '../../../types/process/order-data';
import { OrderStatus as MysqlOrderStatus, OrderStatusMap as MysqlOrderStatusMap } from '../../../types/process/order';
import { OperationAction } from '../../../types/process/operation-log';
import * as _ from 'lodash';
import { buildNumericMapOptions } from '../../../utils/format.utils';
import { useNavigate } from 'react-router-dom';


export type OrderTriggerTarget =
  | 'statusToAudit' // 提交审核至待审池
  | 'auditToLock' // 接单并去审核/继续审核
  | 'auditRelease' // 释放订单回待审池
  | 'auditView' // 查看审核订单
  | 'reportView' // 查看风控报告
  | 'reportToBuer' // 获取布尔数据
  | 'syncRrzuStatus' // 同步人人租状态到工单
  | 'cancelByOrder' // 取消订单/查看取消原因
  | 'cancelByShipIntercept' // 发货拦截/查看拦截原因
  | 'cancelByLogisticsIntercept' // 物流拦截/查看拦截原因
  | 'cancelRevoke' // 撤销取消申请
  | 'decisionAction' // 审核主管决策/去发货
  | 'decisionInfoView' // 查看决策信息
  | 'orderReaudit' // 订单复审
  | 'deliveryFollowup' // 催促发货跟进
  | 'viewReceiptEvidence'; // 查看签收凭证

export type OrderTriggerLayout = 'list' | 'table';

type OpenCancelModalArgs = {
  order: OrderEntity;
  template?: CancelOrderTemplateKey;
  mode?: 'edit' | 'view';
  confirmText?: string;
  onConfirm?: () => Promise<void> | void;
};

type OpenDecisionModalArgs = {
  order: MergedOrder;
  source?: 'decision' | 'delivery';
};

type OpenPlatformLogModalArgs = {
  orderNo: string;
  type?: string;
  title?: string;
};

type OpenDecisionInfoModalArgs = {
  order: MergedOrder;
};

type OrderTriggerContextValue = {
  openCancelModal?: (args: OpenCancelModalArgs) => void;
  openDecisionModal?: (args: OpenDecisionModalArgs) => void;
  openDecisionInfoModal?: (args: OpenDecisionInfoModalArgs) => void;
  openPlatformLogModal?: (args: OpenPlatformLogModalArgs) => void;
  onReload?: () => void;
};

const OrderTriggerContext = createContext<OrderTriggerContextValue>({});

export const OrderTriggerActionProvider: React.FC<{
  children: React.ReactNode;
  onReload?: () => void;
}> = ({ children, onReload }) => {
  const cancelModalRef = useRef<CancelOrderModalRef>(null);
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);
  const [decisionModalArgs, setDecisionModalArgs] = useState<OpenDecisionModalArgs | null>(null);
  const [decisionInfoModalVisible, setDecisionInfoModalVisible] = useState(false);
  const [decisionInfoModalArgs, setDecisionInfoModalArgs] = useState<OpenDecisionInfoModalArgs | null>(null);
  const [platformLogModalVisible, setPlatformLogModalVisible] = useState(false);
  const [platformLogModalArgs, setPlatformLogModalArgs] = useState<OpenPlatformLogModalArgs | null>(null);

  const value = useMemo<OrderTriggerContextValue>(
    () => ({
      onReload,
      openCancelModal: (args) => cancelModalRef.current?.open(args),
      openDecisionModal: (args) => {
        setDecisionModalArgs(args);
        setDecisionModalVisible(true);
      },
      openDecisionInfoModal: (args) => {
        setDecisionInfoModalArgs(args);
        setDecisionInfoModalVisible(true);
      },
      openPlatformLogModal: (args) => {
        setPlatformLogModalArgs(args);
        setPlatformLogModalVisible(true);
      },
    }),
    [onReload]
  );

  return (
    <OrderTriggerContext.Provider value={value}>
      {children}
      <CancelOrderModal ref={cancelModalRef} onSuccess={onReload} />
      {decisionModalArgs && (
        <DecisionModal
          visible={decisionModalVisible}
          order={decisionModalArgs.order}
          source={decisionModalArgs.source}
          onCancel={() => {
            setDecisionModalVisible(false);
            setDecisionModalArgs(null);
          }}
          onSuccess={() => {
            setDecisionModalVisible(false);
            setDecisionModalArgs(null);
            onReload?.();
          }}
        />
      )}
      {decisionInfoModalArgs && (
        <DecisionInfoModal
          visible={decisionInfoModalVisible}
          order={decisionInfoModalArgs.order}
          onCancel={() => {
            setDecisionInfoModalVisible(false);
            setDecisionInfoModalArgs(null);
          }}
        />
      )}
      {platformLogModalArgs && (
        <PlatformLogModal
          visible={platformLogModalVisible}
          onCancel={() => {
            setPlatformLogModalVisible(false);
            setPlatformLogModalArgs(null);
          }}
          orderNo={platformLogModalArgs.orderNo}
          type={platformLogModalArgs.type}
          title={platformLogModalArgs.title}
          onSuccess={() => {
            // Optional: reload list if needed, but log modal usually doesn't affect list status
          }}
        />
      )}
    </OrderTriggerContext.Provider>
  );
};

export interface OrderTriggerActionMapProps {
  targets: OrderTriggerTarget[];
  order: MergedOrder;
  layout?: OrderTriggerLayout;
  ctx?: OrderListActionContext;
  onReload?: () => void;
}

const viewReportStyle = { background: '#1ab9c4ff', color: 'white', border: 'none' } as const;
const greenPrimaryStyle = { background: '#52c41a', borderColor: '#52c41a' } as const;

const OrderTriggerActionMap: React.FC<OrderTriggerActionMapProps> = ({
  targets,
  order,
  layout = 'list',
  ctx,
  onReload,
}) => {
  const { staff, can } = useAuth();
  const currentUserId = staff?.id || 0;
  const { staffMap } = useStaffMap();
  const navigate = useNavigate();
  const context = useContext(OrderTriggerContext);
  const effectiveReload = onReload || context.onReload;
  const openCancelModal = context.openCancelModal;
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncNextStatus, setSyncNextStatus] = useState<number | undefined>(undefined);
  const [syncSaving, setSyncSaving] = useState(false);

  const buttonBaseProps = useMemo(() => {
    if (layout === 'table') {
      return { type: 'link' as const, size: 'small' as const, block: false as const, style: { padding: 0 } };
    }
    return { size: 'small' as const, block: true as const };
  }, [layout]);

  const container = useMemo(() => {
    if (layout === 'table') return { direction: 'vertical' as const, size: 2 as const, align: 'start' as const };
    return { direction: 'vertical' as const, style: { width: '100%' } as const };
  }, [layout]);

  const triggerMap: Record<OrderTriggerTarget, () => React.ReactNode | null> = {
    auditRelease: () => {
      const auditOwnerId = order.order.auditId || 0;
      const auditStatus = order.order.auditStatus || 0;
      const isMine = currentUserId > 0 && auditOwnerId === currentUserId;
      const canRelease = isMine
        && order.order.orderStatus === 3
        && _.includes([1, 2], auditStatus);
      if (!canRelease) return null;

      const onRelease = () => {
        Modal.confirm({
          title: '确认释放订单？',
          content: '释放后订单将回到待审池，当前审核计时将结束。',
          okText: '确认释放',
          okButtonProps: { danger: true },
          cancelText: '取消',
          onOk: async () => {
            try {
              await orderApi.changeStatus(order.order.orderNo, OperationAction.ReleaseAudit);
              message.success('已释放订单');
              effectiveReload?.();
            } catch (e: any) {
              const msg = e?.response?.data?.message || e?.message || '释放订单失败';
              message.error(msg);
              throw e;
            }
          },
        });
      };

      return (
        <Button
          {...buttonBaseProps}
          danger
          type={layout === 'table' ? 'link' : undefined}
          onClick={onRelease}
        >
          释放订单
        </Button>
      );
    },
    auditToLock: () => {
      const auditOwnerId = order.order.auditId || 0;
      const auditStatus = order.order.auditStatus || 0;
      const isMine = currentUserId > 0 && auditOwnerId === currentUserId;
      const isLockedByOther = auditOwnerId > 0 && auditOwnerId !== currentUserId;

      if (isLockedByOther) {
        const lockedStaff = staffMap?.get(auditOwnerId);
        const auditorName = lockedStaff
          ? (lockedStaff.name || lockedStaff.username || `ID:${auditOwnerId}`)
          : `ID:${auditOwnerId}`;
        const label = auditorName ? `${auditorName}审核中` : '审核中';
        return (
          <Button {...buttonBaseProps} disabled>
            {label}
          </Button>
        );
      }

      if (isMine && auditStatus === 2) {
        return (
          <Button
            {...buttonBaseProps}
            type={layout === 'table' ? 'link' : 'primary'}
            onClick={() => window.open(`/order/audit/${order.order.orderNo}`, '_blank')}
          >
            继续审核
          </Button>
        );
      }

      if (isMine && auditStatus === 1) {
        return (
          <Button
            {...buttonBaseProps}
            type={layout === 'table' ? 'link' : 'primary'}
            onClick={() => window.open(`/order/audit/${order.order.orderNo}`, '_blank')}
          >
            去审核
          </Button>
        );
      }

      const canAcceptAndAudit = auditOwnerId === 0 && auditStatus === 0 && order.order.orderStatus === 3;
      if (!canAcceptAndAudit) return null;

      const onAcceptAndAudit = async () => {
        if (!currentUserId) {
          message.error('未获取到当前用户信息');
          return;
        }

        try {
          await orderApi.changeStatus(order.order.orderNo, OperationAction.LockAudit);
          window.open(`/order/audit/${order.order.orderNo}`, '_blank');
          effectiveReload?.();
        } catch (e: any) {
          const msg = e?.response?.data?.message || e?.message || '接单失败';
          if (String(msg).includes('在审')) {
            Modal.warning({
              title: '当前存在在审订单',
              content: msg,
            });
            return;
          }
          message.error(msg);
        }
      };

      return (
        <Button
          {...buttonBaseProps}
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => void onAcceptAndAudit()}
        >
          接单并去审核
        </Button>
      );
    },
    auditView: () => (
      <Button
        {...buttonBaseProps}
        type={layout === 'table' ? 'link' : 'primary'}
        onClick={() => window.open(`/order/audit/${order.order.orderNo}`, '_blank')}
      >
        {layout === 'table' ? '审核' : '查看审核订单'}
      </Button>
    ),

    reportView: () => (
      <Button
        {...buttonBaseProps}
        style={layout === 'table' ? undefined : viewReportStyle}
        onClick={() => window.open(`/order/report/${order.order.orderNo}`, '_blank')}
      >
        {layout === 'table' ? '报告' : '查看风控报告'}
      </Button>
    ),

    statusToAudit: () => {
      if (order.order.cancelType) return null;
      const onSubmit = async () => {
        try {
          await orderApi.changeStatus(order.order.orderNo, OperationAction.ToAudit);
          showActionFeedback({
            title: '已提交至待审核订单池',
            buttons: [{ label: '查看', onClick: () => window.open(`/order/audit/wait`, '_blank') }],
            onClose: effectiveReload,
          });
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || '提交失败');
        }
      };

      return (
        <Button
          {...buttonBaseProps}
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => void onSubmit()}
        >
          提交审核
        </Button>
      );
    },

    cancelByShipIntercept: () => {
      if (order.order.cancelType) {
        if (order.order.cancelType !== 'SHIP_INTERCEPT') return null;
        if (!openCancelModal) return null;
        return (
          <Button {...buttonBaseProps} onClick={() => openCancelModal({ order: order.order, mode: 'view' })}>
            {layout === 'table' ? '发货拦截原因' : '查看发货拦截原因'}
          </Button>
        );
      }
      if (order.orderData?.orderStatus !== OrderStatus.WaitSend) return null;
      if (!_.includes([MysqlOrderStatus.PreProcess, MysqlOrderStatus.Audit], order.order.orderStatus)) return null;
      if (!openCancelModal) return null;
      return (
        <Button
          {...buttonBaseProps}
          danger
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => openCancelModal({ order: order.order, template: 'shipIntercept' })}
        >
          发货拦截
        </Button>
      );
    },

    cancelByLogisticsIntercept: () => {
      if (order.order.cancelType) {
        if (order.order.cancelType !== 'LOGISTICS_INTERCEPT') return null;
        if (!openCancelModal) return null;
        return (
          <Button {...buttonBaseProps} onClick={() => openCancelModal({ order: order.order, mode: 'view' })}>
            {layout === 'table' ? '物流拦截原因' : '查看物流拦截原因'}
          </Button>
        );
      }
      if (order.orderData?.orderStatus !== OrderStatus.WaitReceive) return null;
      if (!_.includes([MysqlOrderStatus.PreProcess, MysqlOrderStatus.Audit], order.order.orderStatus)) return null;
      if (!openCancelModal) return null;
      return (
        <Button
          {...buttonBaseProps}
          danger
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => openCancelModal({ order: order.order, template: 'logisticsIntercept' })}
        >
          物流拦截
        </Button>
      );
    },

    cancelByOrder: () => {
      if (!openCancelModal) return null;
      if (!_.includes([MysqlOrderStatus.PreProcess, MysqlOrderStatus.Audit, MysqlOrderStatus.Decision], order.order.orderStatus)) return null;

      if (order.order.cancelType) {
        if (order.order.cancelType !== 'CANCEL_ORDER') return null;
        return (
          <Button {...buttonBaseProps} onClick={() => openCancelModal({ order: order.order, mode: 'view' })}>
            {'查看取消原因'}
          </Button>
        );
      }

      return (
        <Button
          {...buttonBaseProps}
          danger
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => openCancelModal({ order: order.order, template: 'cancelOrder' })}
        >
          {layout === 'table' ? '取消' : '提交取消申请'}
        </Button>
      );
    },

    reportToBuer: () => {
      if (order.order.cancelType) return null;
      const buerReport = order.buerReport;
      const buerReportSuccess = buerReport?.status === 4;
      const buerReportExpired = (() => {
        if (!buerReport?.createdAt) return true;
        const createdAt = dayjs(buerReport.createdAt);
        if (!createdAt.isValid()) return true;
        return dayjs().diff(createdAt, 'minute') >= 10;
      })();
      if (buerReport && (buerReportSuccess || !buerReportExpired)) return null;
      const onGenerate = async () => {
        const hide = message.loading('正在生成布尔报告...', 0);
        try {
          await externalReportApi.generateReport(order.order.orderNo);
          showActionFeedback({
            title: '已提交布尔报告生成任务',
            buttons: [{ label: '查看', onClick: () => window.open(`/order/report/${order.order.orderNo}`, '_blank') }],
            onClose: effectiveReload,
          });
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || '生成失败');
        } finally {
          hide();
        }
      };

      return (
        <Button
          {...buttonBaseProps}
          type={layout === 'table' ? 'link' : 'primary'}
          style={layout === 'table' ? undefined : greenPrimaryStyle}
          onClick={() => void onGenerate()}
        >
          获取布尔数据
        </Button>
      );
    },

    syncRrzuStatus: () => {
      if (!can('action.orders.changeStatus')) return null;
      // const canSync = _.includes([MysqlOrderStatus.PreProcess, MysqlOrderStatus.Audit], order.order.orderStatus)
      //   && order.orderData?.orderStatus && order.orderData.orderStatus > OrderStatus.WaitReceive && !_.includes([OrderStatus.WaitRisk, OrderStatus.CreditAuth], order.orderData.orderStatus);
      // if (!canSync) return null;

      const statusOptions = buildNumericMapOptions(MysqlOrderStatusMap, { excludedValues: [91] });
      const currentStatusLabel = MysqlOrderStatusMap[order.order.orderStatus] || String(order.order.orderStatus);

      const openSyncModal = () => {
        setSyncNextStatus(order.order.orderStatus);
        setSyncModalOpen(true);
      };

      const onConfirm = async () => {
        if (!syncNextStatus) {
          message.warning('请选择工单状态');
          return;
        }
        setSyncSaving(true);
        const hide = message.loading('正在更新工单状态...', 0);
        try {
          const res = await orderApi.changeStatus(order.order.orderNo, OperationAction.SyncRrzuStatus, { orderStatus: syncNextStatus });
          showActionFeedback({
            title: res?.message || '同步成功',
            buttons: effectiveReload
              ? [{ label: '刷新', onClick: effectiveReload }]
              : undefined,
            onClose: effectiveReload,
          });
          setSyncModalOpen(false);
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || '同步失败');
        } finally {
          hide();
          setSyncSaving(false);
        }
      };

      return (
        <>
          <Button
            {...buttonBaseProps}
            danger
            type={layout === 'table' ? 'link' : 'primary'}
            onClick={openSyncModal}
          >
            修正工单状态
          </Button>
          <Modal
            title="修正工单状态"
            open={syncModalOpen}
            onCancel={() => setSyncModalOpen(false)}
            onOk={onConfirm}
            confirmLoading={syncSaving}
            okText="确认修改"
            cancelText="取消"
            destroyOnClose
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="订单号">{order.order.orderNo}</Descriptions.Item>
                <Descriptions.Item label="当前工单状态">{currentStatusLabel}</Descriptions.Item>
              </Descriptions>
              <Select
                placeholder="请选择系统工单状态"
                options={statusOptions}
                value={syncNextStatus}
                onChange={setSyncNextStatus}
                style={{ width: '100%' }}
              />
            </Space>
          </Modal>
        </>
      );
    },

    cancelRevoke: () => {
      if (!can('action.orders.changeStatus')) return null;
      if (order.order.orderStatus === MysqlOrderStatus.Cancelled) return null;
      const hasCancel = (order.order.cancelStatus || 0) > 0 || !!order.order.cancelType;
      if (!hasCancel) return null;
      if (!openCancelModal) return null;
      return (
        <Button
          {...buttonBaseProps}
          danger
          type={layout === 'table' ? 'link' : 'primary'}
          onClick={() => openCancelModal({
            order: order.order,
            mode: 'view',
            confirmText: '撤销取消申请',
            onConfirm: async () => {
              await orderApi.changeStatus(order.order.orderNo, OperationAction.CancelRevoke);
              message.success('已撤销取消申请');
              effectiveReload?.();
            },
          })}
        >
          撤销取消申请
        </Button>
      );
    },

    decisionAction: () => {
      if (!context.openDecisionModal) return null;

      const onOpen = () => {
        context.openDecisionModal?.({
          order: order,
          source: (window.location.pathname.includes('delivery')) ? 'delivery' : 'decision'
        });
      };

      return (
        <Button
          {...buttonBaseProps}
          type="primary"
          onClick={onOpen}
        >
          去决策
        </Button>
      );
    },

    decisionInfoView: () => {
      if (!context.openDecisionInfoModal) return null;

      const onOpen = () => {
        context.openDecisionInfoModal?.({
          order: order,
        });
      };

      return (
        <Button
          {...buttonBaseProps}
          type={layout === 'table' ? 'link' : undefined}
          icon={layout === 'table' ? <FileTextOutlined /> : undefined}
          onClick={onOpen}
        >
          查看决策信息
        </Button>
      );
    },

    orderReaudit: () => (
      <Button
        {...buttonBaseProps}
        type={layout === 'table' ? 'link' : 'primary'}
        onClick={() => navigate(`/order/audit/${order.order.orderNo}?scene=reaudit`, { replace: true })}
      >
        订单复审
      </Button>
    ),

    deliveryFollowup: () => (
      <Button
        {...buttonBaseProps}
        type={layout === 'table' ? 'link' : undefined}
        icon={layout === 'table' ? <FormOutlined /> : undefined}
        onClick={() => context.openPlatformLogModal?.({ orderNo: order.order.orderNo, type: 'urge_delivery', title: '催促发货跟进' })}
      >
        {layout === 'table' ? '记录跟进' : '催促发货跟进'}
      </Button>
    ),

    viewReceiptEvidence: () => {
      const evidenceList = order.orderRecord?.evidence || [];
      const evidence = evidenceList.find((e: any) => String(e.phase) === '9' || String(e.type) === '9');
      const evidenceUrl = evidence?.origin_url || order.order.receiptEvidence;

      if (!evidenceUrl) {
        return (
          <span style={{ color: '#ff4d4f', fontSize: 13 }}>请前往人人租平台上传</span>
        );
      }

      const onView = () => {
        Modal.info({
          title: '查看凭证',
          content: (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Image src={evidenceUrl} alt="凭证" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          ),
          width: 600,
          icon: null,
          okText: '关闭',
          maskClosable: true,
        });
      };

      return (
        <Button
          {...buttonBaseProps}
          type={layout === 'table' ? 'link' : undefined}
          icon={layout === 'table' ? <PictureOutlined /> : undefined}
          onClick={onView}
        >
          查看凭证
        </Button>
      );
    },

  };

  const nodes = targets
    .map((t) => ({ target: t, node: triggerMap[t]?.() }))
    .filter((x): x is { target: OrderTriggerTarget; node: React.ReactNode } => x.node !== null && x.node !== undefined);

  if (nodes.length === 0) return null;

  return (
    <Space {...container}>
      {nodes.map((n, i) => (
        <React.Fragment key={`${n.target}_${i}`}>{n.node}</React.Fragment>
      ))}
    </Space>
  );
};

export default OrderTriggerActionMap;
